import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { Mail, Lock, LogIn, ShieldCheck, ArrowRight, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]     = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate  = useNavigate();
  const appName   = process.env.REACT_APP_APP_NAME || 'CRM Automax';

  const roleRoutes = {
    'master-admin':    '/master-admin',
    'super-admin':     '/admin/dashboard',
    'manager':         '/manager/dashboard',
    'executive':       '/executive/dashboard',
    'telecaller':      '/telecaller/leads',
    'site-visit-agent':'/site-visit/leads',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorCode('');

    try {
      const response = await api('/users/login', { method: 'POST', body: { email, password } });
      const data = await response.json();

      if (response.ok) {
        login(data.user, data.token, data.subscription);
        navigate(roleRoutes[data.user.role] || '/');
      } else {
        if (data.code === 'PLAN_EXPIRED' || data.code === 'NO_SUBSCRIPTION') {
          setErrorCode(data.code);
        }
        setError(data.message || 'Login failed');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '13px 13px 13px 44px',
    borderRadius: '10px', border: '1px solid #e2e8f0',
    fontSize: '14px', outline: 'none',
    background: '#ffffff', color: '#1e293b',
    transition: 'all 0.2s ease', boxSizing: 'border-box',
  };

  return (
    <div className="auth-page-wrapper" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f8fafc',
      padding: '24px', fontFamily: "'Inter', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(15px);}to{opacity:1;transform:translateY(0);} }
        .login-input:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 4px rgba(59,130,246,0.1); }
        .login-btn:hover:not(:disabled) { background: #2563eb !important; transform: translateY(-1px); }
        .login-btn:active:not(:disabled) { transform: translateY(0); }
      `}</style>

      <div className="auth-card-container" style={{
        width: '100%', maxWidth: '400px',
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '20px', padding: '48px 36px',
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.05)',
        animation: 'fadeUp 0.5s ease-out',
      }}>

        {/* Logo + Title */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '14px',
            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', boxShadow: '0 8px 20px rgba(59,130,246,0.2)',
          }}>
            <ShieldCheck size={26} color="white" />
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', marginBottom: '8px', letterSpacing: '-0.02em' }}>
            {appName}
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px' }}>Welcome back! Please enter your details.</p>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#fef2f2',
            color: '#b91c1c', padding: '14px 16px', borderRadius: '10px',
            fontSize: '13px', marginBottom: '24px',
            border: '1px solid #fee2e2',
            lineHeight: 1.5,
          }}>
            {error}
            {(errorCode === 'PLAN_EXPIRED' || errorCode === 'NO_SUBSCRIPTION') && (
              <div style={{ marginTop: '10px' }}>
                <button
                  onClick={() => navigate('/plan-expired')}
                  style={{
                    background: '#ffffff', color: '#b91c1c',
                    border: '1px solid #fecaca',
                    borderRadius: '8px', padding: '6px 12px',
                    fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                  }}
                >
                  Renew Subscription →
                </button>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          <div>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '8px', marginLeft: '2px' }}>
              Email Address
            </label>
            <div style={{ position: 'relative' }}>
              <Mail size={17} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                className="login-input" type="email"
                value={email} onChange={e => setEmail(e.target.value)}
                required placeholder="name@company.com" style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569', display: 'block', marginBottom: '8px', marginLeft: '2px' }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <Lock size={17} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                className="login-input" type={showPassword ? 'text' : 'password'}
                value={password} onChange={e => setPassword(e.target.value)}
                required placeholder="••••••••" style={{...inputStyle, paddingRight: '40px'}}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
            <div style={{ textAlign: 'right', marginTop: '8px' }}>
              <Link to="/forgot-password" style={{ fontSize: '12px', fontWeight: '600', color: '#3b82f6', textDecoration: 'none' }}>
                Forgot Password?
              </Link>
            </div>
          </div>

          <button
            type="submit" disabled={loading} className="login-btn"
            style={{
              width: '100%', padding: '14px',
              background: loading ? '#93c5fd' : '#3b82f6',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '15px', fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              marginTop: '4px', transition: 'all 0.2s ease',
            }}
          >
            {loading ? 'Authenticating...' : <><LogIn size={18} /> Sign In</>}
          </button>
        </form>

        <div style={{ marginTop: '32px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
          Don't have an account?{' '}
          <Link to="/pricing" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}>
            Start 14-day trial
          </Link>
        </div>

        {/* Legal Footer Links */}
        <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '16px' }}>
          {[
            { label: 'Privacy Policy', path: '/privacy-policy' },
            { label: 'Terms', path: '/terms' },
            { label: 'Payment Policy', path: '/payment-policy' },
          ].map(lnk => (
            <Link key={lnk.path} to={lnk.path} style={{ fontSize: '12px', color: '#94a3b8', textDecoration: 'none', transition: 'color 0.2s ease' }}
              onMouseEnter={e => e.target.style.color='#64748b'}
              onMouseLeave={e => e.target.style.color='#94a3b8'}
            >{lnk.label}</Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Login;

